import React from 'react';
import { Alert, Button, Card, Col, Row, Skeleton, Space, Tag, Typography, theme } from 'antd';
import { AndroidFilled, AppleFilled, CheckCircleFilled, ExportOutlined, ReloadOutlined } from '@ant-design/icons';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { usePublicationStatus } from '../../../../hooks/leedflow/usePublicationStatus';
import type {
  MinimumVerdict,
  PlatformPublication,
  PublicationRelease,
  PublicationSeverity,
} from '../../../../../domain/use_cases/leedflow/overview/getPublicationStatus';

const { useToken } = theme;
const { Text } = Typography;

/*
 * Onglet « Publication » — où en sont les derniers builds sur les deux stores, et à partir de
 * quand la version minimum peut être montée sans enfermer les utilisateurs dehors
 * (`mobile/docs/force-update.md`).
 */

const SEVERITY_COLORS: Record<PublicationSeverity, string> = {
  live: 'green',
  pending: 'processing',
  action: 'gold',
  rejected: 'red',
  neutral: 'default',
};

const ReleaseRow: React.FC<{ release: PublicationRelease }> = ({ release }) => {
  const { token } = useToken();

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        borderRadius: token.borderRadiusSM,
        padding: '8px 10px',
        background: token.colorFillTertiary,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{release.version}</span>
        {release.build && (
          <Text type="secondary" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
            build {release.build}
          </Text>
        )}
      </span>
      {/* L'état brut en title : lever un doute sur un libellé sans ouvrir la console. */}
      <Tag
        color={SEVERITY_COLORS[release.severity]}
        icon={release.severity === 'live' ? <CheckCircleFilled /> : undefined}
        title={release.state}
        style={{ marginInlineEnd: 0 }}
      >
        {release.label}
      </Tag>
    </div>
  );
};

/** Le lien qu'on envoie à un client : celui-là même que la pop-up de mise à jour forcée ouvre. */
const StoreLink: React.FC<{ storeUrl: string | null }> = ({ storeUrl }) => {
  const { token } = useToken();

  if (!storeUrl) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        Lien de téléchargement non renseigné (Admins → Configurations → Application → Stores).
      </Text>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        borderRadius: token.borderRadiusSM,
        padding: '6px 10px',
        background: token.colorFillQuaternary,
      }}
    >
      <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        Lien client
      </Text>
      <Typography.Text
        copyable={{ text: storeUrl, tooltips: ['Copier le lien', 'Copié'] }}
        ellipsis
        style={{ fontSize: 12, flex: 1, minWidth: 0 }}
      >
        <a href={storeUrl} target="_blank" rel="noreferrer">
          {storeUrl}
        </a>
      </Typography.Text>
    </div>
  );
};

const MinimumVersion: React.FC<{ platform: PlatformPublication }> = ({ platform }) => {
  const { token } = useToken();

  // `ahead` est l'alarme : la version minimum dépasse ce que le store sert, donc le parc est
  // déjà bloqué sans mise à jour à installer.
  const BORDERS: Record<MinimumVerdict, string> = {
    aligned: token.colorSuccess,
    behind: token.colorWarning,
    ahead: token.colorError,
    unknown: token.colorBorder,
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        border: `1px solid ${BORDERS[platform.minimumVerdict]}`,
        borderRadius: token.borderRadiusSM,
        padding: '6px 10px',
      }}
    >
      <Text type="secondary" style={{ fontSize: 12 }}>
        Version minimum forcée
      </Text>
      {platform.minimumVersion ? (
        <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>{platform.minimumVersion}</Text>
      ) : (
        <Text type="secondary">aucune</Text>
      )}
      {platform.minimumVerdict === 'behind' && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          — le store sert déjà la {platform.liveVersion}
        </Text>
      )}
      {platform.minimumVerdict === 'ahead' && (
        <Text type="danger" style={{ fontSize: 12 }}>
          — au-delà de la {platform.liveVersion} servie par le store, le parc est bloqué
        </Text>
      )}
    </div>
  );
};

interface PlatformCardProps {
  title: string;
  icon: React.ReactNode;
  appIconUrl: string | null;
  platform: PlatformPublication;
  status: ContextStatus;
  /** Ce qu'il faut faire quand les identifiants du store manquent côté backend. */
  missingCredentials: string;
}

const PlatformCard: React.FC<PlatformCardProps> = ({ title, icon, appIconUrl, platform, status, missingCredentials }) => {
  const { token } = useToken();

  const body = () => {
    if (status === ContextStatus.LOADING) return <Skeleton active paragraph={{ rows: 4 }} />;

    if (!platform.configured) {
      return <Alert type="info" showIcon message="Store non connecté" description={missingCredentials} />;
    }

    if (platform.error) {
      return (
        <Alert
          type="error"
          showIcon
          message="Store injoignable"
          description={
            <>
              L'autre plateforme reste affichée, elle est interrogée séparément.
              <div style={{ marginTop: 8, fontFamily: 'monospace', fontSize: 12 }}>{platform.error}</div>
            </>
          }
        />
      );
    }

    if (platform.releases.length === 0) {
      return (
        <div style={{ color: token.colorTextSecondary, textAlign: 'center', padding: '2rem 0' }}>
          Aucune version ouverte sur ce store.
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {platform.releases.map((release, index) => (
          <ReleaseRow key={`${release.track ?? 'ios'}-${index}`} release={release} />
        ))}
      </div>
    );
  };

  return (
    <Card
      title={
        <Space>
          {appIconUrl && (
            <img
              src={appIconUrl}
              alt=""
              width={28}
              height={28}
              style={{ borderRadius: 6, display: 'block' }}
            />
          )}
          {icon}
          {title}
        </Space>
      }
      extra={
        platform.consoleUrl && (
          <a href={platform.consoleUrl} target="_blank" rel="noreferrer">
            Console <ExportOutlined />
          </a>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        <MinimumVersion platform={platform} />
        <StoreLink storeUrl={platform.storeUrl} />
      </div>
      {body()}
    </Card>
  );
};

const PublicationTab: React.FC = () => {
  const { status, contextStatus, reload } = usePublicationStatus();

  // Le backend tranche, l'onglet affiche. Un relevé en échec ne conseille rien : le bandeau
  // resterait sinon à l'écran sur des données vieilles de plusieurs heures.
  const showRaiseBanner = status.canRaiseMinimum && contextStatus === ContextStatus.SUCCESS;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <Text type="secondary">
          {status.fetchedAt > 0 && `Relevé à ${new Date(status.fetchedAt).toLocaleTimeString('fr-FR')}. `}
          Les stores sont interrogés au plus toutes les 5 minutes, leur quota est serré.
        </Text>
        <Button icon={<ReloadOutlined />} onClick={reload} loading={contextStatus === ContextStatus.LOADING}>
          Actualiser
        </Button>
      </div>

      {contextStatus === ContextStatus.ERROR && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: '1rem' }}
          message="Impossible de récupérer l'état de publication"
          description="La route /leedflow/publication-status n'a pas répondu. Le backend est peut-être plus ancien que cet écran."
        />
      )}

      {showRaiseBanner && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: '1rem' }}
          message={`La ${status.ios.liveVersion} est disponible sur les deux stores`}
          description="La version minimum peut être montée sans enfermer personne dehors : Admins → Configurations → Application → Stores, puis « Rafraîchir la configuration » sur le Backend."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <PlatformCard
            title="iOS — App Store"
            icon={<AppleFilled />}
            appIconUrl={status.appIconUrl}
            platform={status.ios}
            status={contextStatus}
            missingCredentials="Ajouter APP_STORE_CONNECT_KEY_ID, APP_STORE_CONNECT_ISSUER_ID et APP_STORE_CONNECT_PRIVATE_KEY au Secret du backend (clé à générer dans App Store Connect → Users and Access → Integrations)."
          />
        </Col>
        <Col xs={24} lg={12}>
          <PlatformCard
            title="Android — Play Store"
            icon={<AndroidFilled />}
            appIconUrl={status.appIconUrl}
            platform={status.android}
            status={contextStatus}
            missingCredentials="Ajouter GOOGLE_PLAY_SERVICE_ACCOUNT (le JSON du service account déjà utilisé par eas submit) au Secret du backend."
          />
        </Col>
      </Row>
    </>
  );
};

export default PublicationTab;
